import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Animated, TextInput } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { BlurView } from 'expo-blur';
import GradientText from './GradientText';

const Dropdown = ({ 
    label, 
    value, 
    placeholder, 
    options = ['Option 1', 'Option 2', 'Option 3'], 
    onSelect,
    style 
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const dropdownRef = useRef(null);
    const focusAnimation = useRef(new Animated.Value(0)).current;
    const optionsAnimation = useRef(new Animated.Value(0)).current;
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => {
      setInputValue(value || '');
  }, [value]);

    const handlePress = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        setIsFocused(newIsOpen);
        
        // Animate focus state
        Animated.timing(focusAnimation, {
            toValue: newIsOpen ? 1 : 0,
            duration: 200,
            useNativeDriver: false,
        }).start();

        // Animate options appearance
        Animated.timing(optionsAnimation, {
            toValue: newIsOpen ? 1 : 0,
            duration: 250,
            useNativeDriver: true,
        }).start();
    };

    const handleSelect = (option) => {
        onSelect(option);
        setIsOpen(false);
        setIsFocused(false);
        
        // Animate focus state
        Animated.timing(focusAnimation, {
            toValue: 0,
            duration: 200,
            useNativeDriver: false,
        }).start();

        // Animate options closing
        Animated.timing(optionsAnimation, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
        }).start();
    };

    const handleBlur = () => {
        setIsFocused(false);
        Animated.timing(focusAnimation, {
            toValue: 0,
            duration: 200,
            useNativeDriver: false,
        }).start();
        
        // Animate options closing
        Animated.timing(optionsAnimation, {
            toValue: 0,
            duration: 200,
            useNativeDriver: true,
        }).start();
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setIsFocused(false);
                Animated.timing(focusAnimation, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: false,
                }).start();
                
                // Animate options closing
                Animated.timing(optionsAnimation, {
                    toValue: 0,
                    duration: 200,
                    useNativeDriver: true,
                }).start();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <View style={[styles.container, style, { zIndex: isOpen ? 1000 : 1 }]} ref={dropdownRef}>
            <GradientText style={styles.label}>{label}</GradientText>
            <View style={styles.dropdownWrapper}>
                <View 
                    style={[
                        styles.dropdown, 
                        isFocused && styles.dropdownFocused,
                        isOpen && styles.dropdownOpen
                    ]}
                >
                    <Animated.View style={styles.gradientContainer}>
                        <LinearGradient 
                            colors={['rgba(0,0,0,0.16)', 'rgba(116,131.83,154,0.16)']} 
                            start={{ x: 0, y: 0 }} 
                            end={{ x: 0, y: 1 }} 
                            style={styles.dropdownGradient} 
                        />
                        <Animated.View 
                            style={[
                                styles.focusedGradient,
                                {
                                    opacity: focusAnimation
                                }
                            ]}
                        >
                            <LinearGradient 
                                colors={['rgba(116,131.83,154,0.16)', 'rgba(0,0,0,0.16)']} 
                                start={{ x: 0, y: 0 }} 
                                end={{ x: 0, y: 1 }} 
                                style={styles.dropdownGradient} 
                            />
                        </Animated.View>
                    </Animated.View>

                    <TextInput
                        style={styles.inputText}
                        value={inputValue}
                        onChangeText={(t) => { setInputValue(t); onSelect && onSelect(t); }}
                        placeholder={placeholder}
                        placeholderTextColor="#787878"
                        onFocus={() => { setIsFocused(true); setIsOpen(true); }}
                        onBlur={handleBlur}
                        autoCapitalize="none"
                        autoCorrect={false}
                    />
                    <TouchableOpacity onPress={handlePress} activeOpacity={0.85}>
                        <Text style={[styles.dropdownArrow, isOpen && styles.dropdownArrowOpen]}>V</Text>
                    </TouchableOpacity>
                </View>

                {isOpen && (
                    <Animated.View 
                        style={[
                            styles.optionsContainer,
                            {
                                opacity: optionsAnimation,
                                transform: [
                                    {
                                        translateY: optionsAnimation.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: [-10, 0],
                                        }),
                                    },
                                    {
                                        scaleY: optionsAnimation.interpolate({
                                            inputRange: [0, 1],
                                            outputRange: [0.8, 1],
                                        }),
                                    },
                                ],
                            }
                        ]}
                    >
                        <View style={styles.optionsList}>
                            {options.map((option, index) => (
                                <TouchableOpacity
                                    key={index}
                                    style={[
                                        styles.option,
                                        index === options.length - 1 && styles.lastOption
                                    ]}
                                    onPress={() => handleSelect(option)}
                                >
                                    <Text style={styles.optionText}>{option}</Text>
                                </TouchableOpacity>
                            ))}
                        </View>
                    </Animated.View>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignSelf: 'stretch',
        position: 'relative',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 8,
    },
    label: {
        color: '#707070',
        fontSize: 18,
        fontFamily: 'Slate',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: 18,
    },
    dropdownWrapper: {
        alignSelf: 'stretch',
        position: 'relative',
    },
    dropdown: {
        alignSelf: 'stretch',
        height: 36,
        paddingLeft: 12,
        paddingRight: 12,
        // background gradient handled by LinearGradient overlay
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        borderRadius: 4,
        overflow: 'hidden',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
    },
    dropdownFocused: {
        borderColor: 'white',
    },
    dropdownOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottomColor: 'rgba(255,255,255,0.1)',
    },
    inputText: {
        flex: 1,
        color: 'white',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: 18,
        ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
    },
    placeholderText: {
        color: '#787878',
    },
    dropdownArrow: {
        color: 'white',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: 18,
        transform: [{ rotate: '0deg' }],
    },
    dropdownArrowOpen: {
        transform: [{ rotate: '180deg' }],
    },
    gradientContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
    },
    dropdownGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
    },
    focusedGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100%',
        height: '100%',
    },
    optionsContainer: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 9999,
        marginTop: -1, // Overlap with dropdown border
        elevation: 10, // For Android
        shadowColor: '#000', // For iOS
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 4,
    },
    optionsList: {
        borderRadius: 4,
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        borderWidth: 1,
        borderTopWidth: 0,
        borderTopColor: 'rgba(255,255,255,0.25)',
        borderLeftColor: 'rgba(255,255,255,0.18)',
        borderRightColor: 'rgba(255,255,255,0.12)',
        borderBottomColor: 'rgba(255,255,255,0.08)',
        backgroundColor: '#000000',
        overflow: 'hidden',
        opacity: 1,
    },
    option: {
        paddingVertical: 10,
        paddingHorizontal: 10,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.1)',
        backgroundColor: '#000000',
    },
    lastOption: {
        borderBottomWidth: 0,
    },
    optionText: {
        color: 'white',
        fontSize: 14,
        fontFamily: 'Slate',
        fontWeight: '500',
        textTransform: 'uppercase',
        lineHeight: 18,
    },
});

export default Dropdown;
